/**
 * generateCyFunReport.ts - génération des documents "Rapport CyFun Ready" et
 * "Plan d'action rapide" en PDF (jsPDF + autoTable) et Word (docx).
 *
 * v2026.06 — A4 PORTRAIT. Structure :
 *   Page de garde → 1. Données entreprise → 2. Résumé par catégorie (+ graphique
 *   + key measures) → 3. Synthèse → 4. Détail contrôle par contrôle (1 catégorie
 *   par page). La preuve = message texte du dernier scan de chaque contrôle.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import {
  Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun,
  HeadingLevel, WidthType, AlignmentType,
} from 'docx';
import { confStats } from './cyfunReportData';
import { buildCover, AUTODOC_STYLES } from './autodocCommon';

/** Résultat détaillé d'un contrôle (M365/Azure) rattaché à une exigence. */
export interface CyFunControlResult {
  id:          string;
  kind:        'm365' | 'azure';
  title:       string;
  status:      string;            // pass | fail | warning | manual | error | not_applicable | na | ''
  message?:    string;            // message du dernier scan = preuve texte
  date?:       string;
  hasEvidence: boolean;
}

export interface CyFunReportRow {
  id:          string;
  level:       string;
  function:    string;
  category:    string;
  requirement: string;
  keyMeasure:  boolean;
  type:        string;       // m365 | azure | manual | external
  conf:        string;       // pass | fail | warning | partial | na | manual | external
  hasEvidence: boolean;
  lastDate?:   string;
  controls?:   string[];
  controlResults?: CyFunControlResult[];
}

/** Ligne d'inventaire de contrôle (tous référentiels confondus). */
export interface CyFunInventoryRow {
  source:   string;   // M365 | Azure | Manuel | Custom
  category: string;
  id:       string;
  title:    string;
  status:   string;   // Conforme | Non conforme | À améliorer | Partiel | Manuel | Non évalué
}

export interface CyFunCompany {
  legalName?:  string;
  bce?:        string;
  address?:    string;
  postalCode?: string;
  city?:       string;
  country?:    string;
  contact?:    string;
}

export interface CyFunReportMeta {
  orgName:     string;
  targetLevel: string;
  date:        string;
  referential?: string;
  company?:    CyFunCompany;
  logo?:       string | null;
}

const BRAND: [number, number, number]  = [31, 61, 92];   // #1F3D5C
const ACCENT: [number, number, number] = [45, 139, 150]; // #2D8B96
const GREEN: [number, number, number]      = [31, 122, 69];   // #1F7A45 - Conforme
const RED: [number, number, number]        = [184, 35, 41];   // #B82329 - Non conforme
const ORANGE: [number, number, number]     = [168, 99, 23];   // #A86317 - À améliorer (legacy)
// v2026.06.165 - Partiel passe à vert clair pour sémantique "en cours, partiellement OK"
const LIGHT_GREEN: [number, number, number] = [110, 168, 74]; // #6EA84A - Partiel
const GREY: [number, number, number]   = [120, 120, 120];
const LIGHT: [number, number, number]  = [205, 205, 205];
const REFERENTIAL_DEFAULT = 'CCB CyberFundamentals (CyFun)';

const TYPE_FR: Record<string, string> = {
  m365: 'Auto M365', azure: 'Auto Azure', manual: 'Manuel', external: 'Externe',
};
const CONF_FR: Record<string, string> = {
  pass: 'Conforme', fail: 'Non conforme', warning: 'À améliorer', partial: 'Partiel',
  na: 'Non évalué', manual: 'Manuel', external: 'Externe',
};
const STATUS_FR: Record<string, string> = {
  pass: 'Conforme', fail: 'Non conforme', error: 'Erreur', warning: 'À améliorer',
  warn: 'À améliorer', manual: 'Manuel', not_applicable: 'Non applicable',
  na: 'Non évalué', external: 'Externe', '': 'Non évalué',
};
function statusFr(s?: string): string { return STATUS_FR[(s ?? '').toLowerCase()] ?? (s ?? 'Non évalué'); }

function fmtDate(d?: string): string {
  if (!d) return '';
  const x = new Date(d);
  return isNaN(x.getTime()) ? '' : x.toLocaleDateString('fr-BE');
}
function safeName(s: string): string { return (s || 'organisation').replace(/[^\w]+/g, '_'); }
function today(): string { return new Date().toISOString().slice(0, 10); }
function companyDisplayName(meta: CyFunReportMeta): string {
  return meta.company?.legalName || meta.orgName || '—';
}
function finalY(doc: jsPDF): number {
  return (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
}

// ===========================================================================
//  RÉSUMÉ PAR CATÉGORIE
// ===========================================================================
interface CatSummary {
  category:     string;
  total:        number;
  conforme:     number;
  partiel:      number;   // partiellement conforme (mix) + à améliorer (warning)
  nonConforme:  number;   // uniquement les échecs francs (fail)
  manuel:       number;
  nonEvalue:    number;
  erreur:       number;   // exigences avec >=1 contrôle en erreur (supplémentaire)
  keyTotal:     number;
  keyOk:        number;
}

function categorySummary(rows: CyFunReportRow[]): CatSummary[] {
  const order: string[] = [];
  const map = new Map<string, CatSummary>();
  for (const r of rows) {
    const cat = r.category || '—';
    if (!map.has(cat)) {
      order.push(cat);
      map.set(cat, { category: cat, total: 0, conforme: 0, partiel: 0, nonConforme: 0, manuel: 0, nonEvalue: 0, erreur: 0, keyTotal: 0, keyOk: 0 });
    }
    const s = map.get(cat)!;
    s.total++;
    if (r.conf === 'pass') s.conforme++;
    else if (r.conf === 'partial' || r.conf === 'warning') s.partiel++;
    else if (r.conf === 'fail') s.nonConforme++;
    else if (r.conf === 'manual') s.manuel++;
    else s.nonEvalue++;
    if ((r.controlResults ?? []).some((c) => (c.status ?? '').toLowerCase() === 'error')) s.erreur++;
    if (r.keyMeasure) { s.keyTotal++; if (r.conf === 'pass') s.keyOk++; }
  }
  return order.map((c) => map.get(c)!);
}

function drawCategoryChartPdf(doc: jsPDF, sums: CatSummary[], startY: number): void {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const left = 14; const barW = W - left - 14;
  let y = startY;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...BRAND);
  doc.text('Répartition par catégorie', left, y); y += 5;
  // Légende
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
  // v2026.06.165 - Partiel = vert clair
  const legend: [string, [number, number, number]][] = [['Conforme', GREEN], ['Partiel', LIGHT_GREEN], ['Non conforme', RED], ['Manuel', GREY], ['Non évalué', LIGHT]];
  let lx = left;
  for (const [lab, col] of legend) { doc.setFillColor(...col); doc.rect(lx, y, 3, 3, 'F'); doc.setTextColor(60, 60, 60); doc.text(lab, lx + 4, y + 3); lx += doc.getTextWidth(lab) + 12; }
  y += 9;

  // Libellé complet sur sa propre ligne, barre pleine largeur en dessous (pas de chevauchement).
  for (const s of sums) {
    if (y > H - 24) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(40, 40, 40);
    doc.text(s.category, left, y + 3);
    // v2026.06.171 - 3 chiffres distincts au lieu de "X/Y conformes" (qui montrait 0 si tout partiel)
    const countTxt = `${s.conforme} conf · ${s.partiel} partiel · ${s.nonConforme} non-conf · /${s.total}`;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(90, 90, 90);
    doc.text(countTxt, W - 14 - doc.getTextWidth(countTxt), y + 3);
    y += 5;
    let x = left; const h = 5;
    const seg = (n: number, col: [number, number, number]) => {
      const w = s.total > 0 ? (n / s.total) * barW : 0;
      if (w > 0) { doc.setFillColor(...col); doc.rect(x, y, w, h, 'F'); x += w; }
    };
    // v2026.06.165 - Partiel = vert clair (LIGHT_GREEN au lieu de ORANGE)
    seg(s.conforme, GREEN); seg(s.partiel, LIGHT_GREEN); seg(s.nonConforme, RED); seg(s.manuel, GREY); seg(s.nonEvalue, LIGHT);
    y += 9;
  }
}

function addCategorySummaryPdf(doc: jsPDF, rows: CyFunReportRow[]): void {
  const sums = categorySummary(rows);
  doc.addPage();
  doc.setFontSize(16); doc.setTextColor(...BRAND); doc.setFont('helvetica', 'bold');
  doc.text('2. Résumé par catégorie', 14, 20);

  autoTable(doc, {
    startY: 26,
    head: [['Catégorie', 'Exig.', 'Conforme', 'Partiel', 'Non conf.', 'Manuel', 'Non éval.', 'Erreur', 'Key en place']],
    body: sums.map((s) => [
      s.category, String(s.total), String(s.conforme), String(s.partiel), String(s.nonConforme),
      String(s.manuel), String(s.nonEvalue), String(s.erreur),
      s.keyTotal > 0 ? `${s.keyOk}/${s.keyTotal}` : '—',
    ]),
    styles: { fontSize: 8, cellPadding: 2, valign: 'middle' },
    headStyles: { fillColor: BRAND, fontSize: 8 },
    columnStyles: { 0: { fontStyle: 'bold' } },
    didParseCell: (data) => {
      if (data.section === 'body') {
        if (data.column.index === 2) data.cell.styles.textColor = GREEN;
        if (data.column.index === 3 && data.cell.raw !== '0') data.cell.styles.textColor = ORANGE;
        if (data.column.index === 4 && data.cell.raw !== '0') data.cell.styles.textColor = RED;
        if (data.column.index === 7 && data.cell.raw !== '0') data.cell.styles.textColor = ORANGE;
      }
    },
  });

  drawCategoryChartPdf(doc, sums, finalY(doc) + 10);
}

// ===========================================================================
//  PAGE DE GARDE (PDF)
// ===========================================================================
// v2026.06.171 - Dessine un bouclier + checkmark stylisé NIS365 (comme favicon.svg)
function drawShieldLogoPdf(doc: jsPDF, x: number, y: number, size: number): void {
  // Bouclier : rectangle haut + triangle pointe en bas (path = lines)
  doc.setFillColor(...ACCENT);
  const topH = size * 0.65;   // partie rectangulaire en haut
  // Coins arrondis simulés avec roundedRect pour la partie supérieure
  doc.roundedRect(x, y, size, topH + 2, 1.5, 1.5, 'F');
  // Triangle pointu vers le bas (drapeau bouclier) - utilisé via .lines pour fill
  const cx = x + size / 2;
  const bottomY = y + size;
  doc.lines(
    [
      [size / 2, bottomY - (y + topH)],     // de (x, y+topH-2) vers cx vers bottom
      [-size / 2, -(bottomY - (y + topH))],
    ],
    x, y + topH - 1,
    [1, 1], 'F', true
  );
  // Checkmark blanc bien centré
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(size * 0.13);
  const ckX1 = x + size * 0.22;
  const ckY1 = y + size * 0.48;
  const ckX2 = x + size * 0.42;
  const ckY2 = y + size * 0.65;
  const ckX3 = x + size * 0.78;
  const ckY3 = y + size * 0.28;
  doc.line(ckX1, ckY1, ckX2, ckY2);
  doc.line(ckX2, ckY2, ckX3, ckY3);
}

function drawCoverPdf(doc: jsPDF, meta: CyFunReportMeta): void {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // v2026.06.171 - Vrai logo bouclier + check (style favicon.svg) au lieu d'un simple rectangle
  doc.setFillColor(...BRAND); doc.rect(0, 0, W, 30, 'F');
  drawShieldLogoPdf(doc, 16, 6, 18);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(17); doc.text('NIS365', 39, 18);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.text('A U D I T O R', 39, 24);

  // Logo de l'organisation (centré au-dessus du titre) si fourni.
  if (meta.logo) {
    try {
      const props = doc.getImageProperties(meta.logo);
      const lw = 24; const lh = Math.max(6, props.height * (lw / props.width));
      const fmt = String(props.fileType || 'PNG').toUpperCase();
      doc.addImage(meta.logo, fmt, (W - lw) / 2, H / 2 - 64, lw, lh);
    } catch { /* logo illisible -> ignore */ }
  }

  doc.setTextColor(...BRAND);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(28);
  doc.text('Rapport CyFun Ready', W / 2, H / 2 - 30, { align: 'center' });

  doc.setFont('helvetica', 'normal'); doc.setFontSize(12); doc.setTextColor(70, 70, 70);
  doc.text(meta.referential ?? REFERENTIAL_DEFAULT, W / 2, H / 2 - 18, { align: 'center' });

  const boxW = 150; const boxX = (W - boxW) / 2; const boxY = H / 2 - 6;
  doc.setDrawColor(...ACCENT); doc.setLineWidth(0.5);
  doc.roundedRect(boxX, boxY, boxW, 42, 3, 3, 'S');
  doc.setFontSize(13); doc.setTextColor(30, 30, 30);
  const cx = W / 2;
  doc.setFont('helvetica', 'bold');
  doc.text(companyDisplayName(meta), cx, boxY + 13, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(11);
  doc.text(`Niveau cible (thème) : ${meta.targetLevel}`, cx, boxY + 24, { align: 'center' });
  doc.text(`Généré le : ${fmtDate(meta.date) || today()}`, cx, boxY + 33, { align: 'center' });

  doc.setFillColor(...BRAND); doc.rect(0, H - 14, W, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(8);
  doc.text('Document confidentiel — généré par NIS365 Auditor', W / 2, H - 5, { align: 'center' });
}

function drawCompanyChapterPdf(doc: jsPDF, meta: CyFunReportMeta): void {
  doc.addPage();
  doc.setTextColor(...BRAND); doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  doc.text('1. Données de l’entreprise', 14, 20);
  const c = meta.company ?? {};
  const ph = '—';
  const rows: [string, string][] = [
    ['Dénomination', c.legalName || meta.orgName || ph],
    ['N° d’entreprise (BCE)', c.bce || ph],
    ['Adresse', c.address || ph],
    ['Code postal / Ville', [c.postalCode, c.city].filter(Boolean).join(' ') || ph],
    ['Pays', c.country || ph],
    ['Contact', c.contact || ph],
  ];
  autoTable(doc, {
    startY: 28,
    body: rows,
    theme: 'plain',
    styles: { fontSize: 11, cellPadding: 3, valign: 'top' },
    columnStyles: { 0: { cellWidth: 55, fontStyle: 'bold', textColor: BRAND }, 1: { cellWidth: 120 } },
  });
  // v2026.06.171 - Hint retiré (le rapport est censé être déjà complet à ce stade,
  // l'admin n'a pas besoin de voir cette indication interne).
}

// ===========================================================================
//  RAPPORT CYFUN READY (PDF, A4 portrait)
// ===========================================================================
export function downloadCyFunReportPdf(rows: CyFunReportRow[], meta: CyFunReportMeta, inventory?: CyFunInventoryRow[]): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  drawCoverPdf(doc, meta);
  drawCompanyChapterPdf(doc, meta);
  addCategorySummaryPdf(doc, rows);

  // --- 3. Synthèse + tableau récapitulatif
  doc.addPage();
  doc.setFontSize(16); doc.setTextColor(...BRAND); doc.setFont('helvetica', 'bold');
  doc.text('3. Synthèse de conformité', 14, 20);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(51, 51, 51);

  const st = confStats(rows);
  doc.text(`Organisation : ${companyDisplayName(meta)}   ·   Niveau cible : ${meta.targetLevel}`, 14, 27);
  doc.text(`Taux de conformité pondéré : ${st.score}%  (sur ${st.evaluated} exigences évaluées)`, 14, 32);
  doc.text(`Exigences : ${st.total}  ·  Conformes : ${st.conformes}  ·  Partiels : ${st.partiels}  ·  Non conformes : ${st.nonConf}  ·  Non évalués : ${st.nonEval}  ·  Key measures : ${st.keyMeasures}`, 14, 37);

  autoTable(doc, {
    startY: 42,
    head: [['Code', 'Niveau', 'Key', 'Type', 'Conformité', 'Preuve', 'Dernier scan', 'Exigence']],
    body: rows.map((r) => [
      r.id, r.level, r.keyMeasure ? 'Oui' : '', TYPE_FR[r.type] ?? r.type,
      CONF_FR[r.conf] ?? r.conf, r.hasEvidence ? 'Oui' : '', fmtDate(r.lastDate), r.requirement,
    ]),
    styles: { fontSize: 6.5, cellPadding: 1.2, valign: 'top', overflow: 'linebreak' },
    columnStyles: {
      0: { cellWidth: 17 }, 1: { cellWidth: 14 }, 2: { cellWidth: 9 }, 3: { cellWidth: 16 },
      4: { cellWidth: 20 }, 5: { cellWidth: 12 }, 6: { cellWidth: 16 }, 7: { cellWidth: 78 },
    },
    headStyles: { fillColor: BRAND, fontSize: 6.5 },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 4) {
        const v = String(data.cell.raw);
        if (v === 'Non conforme') data.cell.styles.textColor = RED;
        else if (v === 'Conforme') data.cell.styles.textColor = GREEN;
      }
    },
  });

  // --- 4. Détail contrôle par contrôle (1 catégorie par page)
  const detailed = rows.filter((r) => (r.controlResults?.length ?? 0) > 0);
  if (detailed.length > 0) {
    const cats: string[] = [];
    const byCat = new Map<string, CyFunReportRow[]>();
    for (const r of detailed) {
      const cat = r.category || '—';
      if (!byCat.has(cat)) { cats.push(cat); byCat.set(cat, []); }
      byCat.get(cat)!.push(r);
    }
    let first = true;
    for (const cat of cats) {
      doc.addPage();
      let y = 20;
      doc.setFontSize(16); doc.setTextColor(...BRAND); doc.setFont('helvetica', 'bold');
      doc.text(first ? '4. Détail contrôle par contrôle' : '4. Détail (suite)', 14, y); y += 7;
      doc.setFontSize(12); doc.setTextColor(...ACCENT);
      doc.text(`Catégorie ${cat}`, 14, y); y += 6;
      first = false;

      for (const r of byCat.get(cat)!) {
        const pageH = doc.internal.pageSize.getHeight();
        if (y > pageH - 40) { doc.addPage(); y = 20; }
        doc.setFontSize(9.5); doc.setTextColor(...BRAND); doc.setFont('helvetica', 'bold');
        const head = `${r.id} — ${r.requirement}`;
        const lines = doc.splitTextToSize(head, 182);
        doc.text(lines, 14, y);
        y += (lines.length as number) * 4 + 1;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(110, 110, 110);
        doc.text(`Niveau ${r.level} · ${CONF_FR[r.conf] ?? r.conf}${r.keyMeasure ? ' · Key measure' : ''}`, 14, y);
        y += 2.5;

        autoTable(doc, {
          startY: y,
          head: [['Contrôle', 'Titre', 'Statut', 'Détail / preuve', 'Date']],
          body: (r.controlResults ?? []).map((c) => [
            c.id, c.title, statusFr(c.status), c.message || (c.hasEvidence ? 'Preuve disponible' : '—'), fmtDate(c.date),
          ]),
          styles: { fontSize: 6.5, cellPadding: 1.2, valign: 'top', overflow: 'linebreak' },
          columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 46 }, 2: { cellWidth: 18 }, 3: { cellWidth: 76 }, 4: { cellWidth: 18 } },
          headStyles: { fillColor: ACCENT, fontSize: 6.5 },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 2) {
              const v = String(data.cell.raw);
              if (v === 'Non conforme' || v === 'Erreur') data.cell.styles.textColor = RED;
              else if (v === 'Conforme') data.cell.styles.textColor = GREEN;
              else if (v === 'À améliorer') data.cell.styles.textColor = ORANGE;
            }
          },
        });
        y = finalY(doc) + 7;
      }
    }
  }

  // --- 5. Inventaire complet des contrôles (identique à la feuille Excel)
  if (inventory && inventory.length > 0) {
    doc.addPage();
    doc.setFontSize(16); doc.setTextColor(...BRAND); doc.setFont('helvetica', 'bold');
    doc.text('5. Inventaire des contrôles', 14, 20);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(51, 51, 51);
    doc.text(`${inventory.length} contrôles (M365, Azure, Gouvernance manuelle).`, 14, 27);
    autoTable(doc, {
      startY: 32,
      head: [['Source', 'Catégorie', 'ID', 'Titre', 'Statut']],
      body: inventory.map((c) => [c.source, c.category, c.id, c.title, c.status]),
      styles: { fontSize: 6.5, cellPadding: 1.2, valign: 'top', overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 16 }, 1: { cellWidth: 38 }, 2: { cellWidth: 22 }, 3: { cellWidth: 82 }, 4: { cellWidth: 24 },
      },
      headStyles: { fillColor: BRAND, fontSize: 6.5 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 4) {
          const v = String(data.cell.raw);
          if (v === 'Non conforme') data.cell.styles.textColor = RED;
          else if (v === 'Conforme') data.cell.styles.textColor = GREEN;
          // v2026.06.165 - Partiel = vert clair, À améliorer reste orange (legacy)
          else if (v === 'Partiel') data.cell.styles.textColor = LIGHT_GREEN;
          else if (v === 'À améliorer') data.cell.styles.textColor = ORANGE;
        }
      },
    });
  }

  doc.save(`Rapport_CyFun_${safeName(companyDisplayName(meta))}_${today()}.pdf`);
}

// ===========================================================================
//  RAPPORT CYFUN READY (Word / docx)
// ===========================================================================
// Cellules de tableau Word réutilisables.
function cell(text: string): TableCell {
  return new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: text ?? '', size: 16 })] })] });
}
function headerCell(text: string): TableCell {
  return new TableCell({
    shading: { fill: '1F3D5C' },
    children: [new Paragraph({ children: [new TextRun({ text: text ?? '', bold: true, color: 'FFFFFF', size: 16 })] })],
  });
}

function docxBar(s: CatSummary): Paragraph[] {
  const N = 42;
  const seg = (n: number) => (s.total > 0 ? Math.round((n / s.total) * N) : 0);
  const g = seg(s.conforme); const p = seg(s.partiel); const r = seg(s.nonConforme); const m = seg(s.manuel);
  const e = Math.max(0, N - g - p - r - m);
  // Libellé complet sur sa propre ligne, barre pleine largeur en dessous (pas de chevauchement).
  return [
    new Paragraph({ spacing: { before: 80 }, children: [
      new TextRun({ text: s.category, bold: true, size: 18, color: '1F3D5C' }),
      // v2026.06.171 - 3 chiffres distincts
      new TextRun({ text: `   ${s.conforme} conf · ${s.partiel} partiel · ${s.nonConforme} non-conf · /${s.total}`, size: 16, color: '666666' }),
    ] }),
    new Paragraph({ children: [
      new TextRun({ text: '█'.repeat(g), color: '1F7A45', size: 16 }),
      new TextRun({ text: '█'.repeat(p), color: '6EA84A', size: 16 }),
      new TextRun({ text: '█'.repeat(r), color: 'B82329', size: 16 }),
      new TextRun({ text: '█'.repeat(m), color: '787878', size: 16 }),
      new TextRun({ text: '█'.repeat(e), color: 'CDCDCD', size: 16 }),
    ] }),
  ];
}

export async function downloadCyFunReportDocx(rows: CyFunReportRow[], meta: CyFunReportMeta, inventory?: CyFunInventoryRow[]): Promise<void> {
  const st = confStats(rows);
  const sums = categorySummary(rows);

  // Page de garde UNIFIÉE (même composant que les AutoDoc) + logo de l'organisation.
  const cover = await buildCover({
    brandLine: 'Rapport de conformité',
    title:     'Rapport CyFun Ready',
    subtitle:  meta.referential ?? REFERENTIAL_DEFAULT,
    orgName:   companyDisplayName(meta),
    logo:      meta.logo,
    boxLines:  [['Niveau cible (thème)', String(meta.targetLevel)]],
    footnote:  'Document généré automatiquement par NIS365 Auditor.',
  });

  const c = meta.company ?? {};
  const ph = '—';
  const companyRows: [string, string][] = [
    ['Dénomination', c.legalName || meta.orgName || ph],
    ['N° d’entreprise (BCE)', c.bce || ph],
    ['Adresse', c.address || ph],
    ['Code postal / Ville', [c.postalCode, c.city].filter(Boolean).join(' ') || ph],
    ['Pays', c.country || ph],
    ['Contact', c.contact || ph],
  ];
  const companyChapter: (Paragraph | Table)[] = [
    new Paragraph({ text: '1. Données de l’entreprise', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: companyRows.map(([k, v]) => new TableRow({ children: [headerCell(k), cell(v)] })) }),
  ];

  // 2. Résumé par catégorie + barres
  const catHeaders = ['Catégorie', 'Exig.', 'Conforme', 'Partiel', 'Non conf.', 'Manuel', 'Non éval.', 'Erreur', 'Key en place'];
  const catChapter: (Paragraph | Table)[] = [
    new Paragraph({ text: '2. Résumé par catégorie', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({ tableHeader: true, children: catHeaders.map((h) => headerCell(h)) }),
        ...sums.map((s) => new TableRow({ children: [
          cell(s.category), cell(String(s.total)), cell(String(s.conforme)), cell(String(s.partiel)), cell(String(s.nonConforme)),
          cell(String(s.manuel)), cell(String(s.nonEvalue)), cell(String(s.erreur)),
          cell(s.keyTotal > 0 ? `${s.keyOk}/${s.keyTotal}` : '—'),
        ] })),
      ],
    }),
    new Paragraph({ children: [] }),
    new Paragraph({ children: [new TextRun({ text: 'Répartition par catégorie', bold: true, size: 20, color: '1F3D5C' })] }),
    new Paragraph({ children: [new TextRun({ text: '█ Conforme  ', color: '1F7A45', size: 14 }), new TextRun({ text: '█ Partiel  ', color: '6EA84A', size: 14 }), new TextRun({ text: '█ Non conforme  ', color: 'B82329', size: 14 }), new TextRun({ text: '█ Manuel  ', color: '787878', size: 14 }), new TextRun({ text: '█ Non évalué', color: 'CDCDCD', size: 14 })] }),
    ...sums.flatMap(docxBar),
  ];

  // 3. Synthèse + tableau
  const headers = ['Code', 'Niveau', 'Key', 'Type', 'Conformité', 'Preuve', 'Dernier scan', 'Exigence'];
  const summaryChapter: (Paragraph | Table)[] = [
    new Paragraph({ text: '3. Synthèse de conformité', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    new Paragraph(`Organisation : ${companyDisplayName(meta)} · Niveau cible : ${meta.targetLevel}`),
    new Paragraph({ children: [new TextRun({ text: `Taux de conformité pondéré : ${st.score}% `, bold: true }), new TextRun(`(Conforme = 1, Partiel = 0,5, sur ${st.evaluated} exigences évaluées)`)] }),
    new Paragraph(`Exigences : ${st.total} · Conformes : ${st.conformes} · Partiels : ${st.partiels} · Non conformes : ${st.nonConf} · Non évalués : ${st.nonEval} · Key measures : ${st.keyMeasures}`),
    new Paragraph({ children: [] }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({ tableHeader: true, children: headers.map((h) => headerCell(h)) }),
        ...rows.map((r) => new TableRow({ children: [
          cell(r.id), cell(r.level), cell(r.keyMeasure ? 'Oui' : ''), cell(TYPE_FR[r.type] ?? r.type),
          cell(CONF_FR[r.conf] ?? r.conf), cell(r.hasEvidence ? 'Oui' : ''), cell(fmtDate(r.lastDate)), cell(r.requirement),
        ] })),
      ],
    }),
  ];

  // 4. Détail contrôle par contrôle (1 catégorie par page)
  const detailChapter: (Paragraph | Table)[] = [];
  const detailed = rows.filter((r) => (r.controlResults?.length ?? 0) > 0);
  if (detailed.length > 0) {
    const cats: string[] = [];
    const byCat = new Map<string, CyFunReportRow[]>();
    for (const r of detailed) {
      const cat = r.category || '—';
      if (!byCat.has(cat)) { cats.push(cat); byCat.set(cat, []); }
      byCat.get(cat)!.push(r);
    }
    let first = true;
    for (const cat of cats) {
      detailChapter.push(new Paragraph({ text: first ? '4. Détail contrôle par contrôle' : `Détail — catégorie ${cat}`, heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
      if (first) detailChapter.push(new Paragraph({ children: [new TextRun({ text: `Catégorie ${cat}`, bold: true, size: 22, color: '2D8B96' })] }));
      first = false;
      for (const r of byCat.get(cat)!) {
        detailChapter.push(new Paragraph({ text: `${r.id} — ${r.requirement}`, heading: HeadingLevel.HEADING_3 }));
        detailChapter.push(new Paragraph({ children: [new TextRun({ text: `Niveau ${r.level} · ${CONF_FR[r.conf] ?? r.conf}${r.keyMeasure ? ' · Key measure' : ''}`, italics: true, size: 18, color: '666666' })] }));
        const ctlHeaders = ['Contrôle', 'Titre', 'Statut', 'Détail / preuve', 'Date'];
        detailChapter.push(new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({ tableHeader: true, children: ctlHeaders.map((h) => headerCell(h)) }),
            ...(r.controlResults ?? []).map((cr) => new TableRow({ children: [
              cell(cr.id), cell(cr.title), cell(statusFr(cr.status)),
              cell(cr.message || (cr.hasEvidence ? 'Preuve disponible' : '—')), cell(fmtDate(cr.date)),
            ] })),
          ],
        }));
        detailChapter.push(new Paragraph({ children: [] }));
      }
    }
  }


  // 5. Inventaire (skipped for now - reconstruction needed in fresh session)
  void inventory;

  const doc = new Document({
    creator: 'NIS365 Auditor',
    title: `Rapport CyFun Ready - ${companyDisplayName(meta)}`,
    styles: AUTODOC_STYLES,
    sections: [{ children: [...cover, ...companyChapter, ...catChapter, ...summaryChapter, ...detailChapter] }],
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `Rapport_CyFun_Ready_${safeName(companyDisplayName(meta))}_${today()}.docx`);
}

export async function downloadCyFunActionPlanPdf(rows: CyFunReportRow[], meta: CyFunReportMeta) {
  void rows; void meta;
  alert('Plan d action PDF non implémenté');
}
export async function downloadCyFunActionPlanDocx(rows: CyFunReportRow[], meta: CyFunReportMeta) {
  void rows; void meta;
  alert('Plan d action Word non implémenté');
}
